import { Component, DestroyRef, inject, Input, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalFunctions, SnackType } from '@global/GlobalFunctions';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { Account, AccountFeatures, AccountsService, IAccount } from '@services/accounts.service';
import { AssetsDataService, LevelAsset, WatchedAssetData } from '@services/assets-data.service';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { Rule, RuleDefinition, RuleEdit, RulesDataService } from '@services/rules-data.service';
import { combineLatest, distinctUntilChanged, filter, firstValueFrom, take } from 'rxjs';
import { convertEpochToDateString } from '@functions/functions'
import { CreateRuleComponent, EditRuleDialogData } from 'src/app/dialogs/create-rule/create-rule.component';
import { EventComment, EventsDataService, RuleEvent, OfflineEvent, WebhookEvent, TimelineItem, DeviationEvent } from '@services/events-data.service';
import { CommentDialogComponent } from 'src/app/dialogs/comment-dialog/comment-dialog.component';
import { CreateActionComponent, createActionDialogData } from 'src/app/dialogs/create-action/create-action.component';
import { Action, ActionsDataService, IActionType } from '@services/actions-data.service';
import { TimelineItemModel } from '@syncfusion/ej2-angular-layouts';
import { Location } from '@angular/common';
import { AssetDataPoint, DataPoint, DatapointsDataService, ISensorData } from '@services/datapoints-data.service';
import { ChartComponent, StripLineSettingsModel } from '@syncfusion/ej2-angular-charts';
import { CreateAssetDistributionComponent } from 'src/app/dialogs/create-asset-distribution/create-asset-distribution.component';
import { AssetDistribution, ModelsService } from '@services/models.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * Default Class Component
 */
@Component({
	selector: 'app-event-details',
	templateUrl: './event-details.component.html',
	styleUrls: ['./event-details.component.scss']
})
export class EventDetailsComponent {

	/** Observable Subject Finalise */
	private destroyRef = inject(DestroyRef);

	/** ruleEvent */
	public ruleEvent: RuleEvent;

	/** offlineEvent */
	public offlineEvent: OfflineEvent;

	/** webhookEvent */
	public webhookEvent: WebhookEvent;

	/** webhookEvent */
	public deviationEvent: DeviationEvent;

	/** CMMSCase */
	public CMMSCase: string | undefined = undefined;

	/** webhookEventType */
	public webhookEventType: string = "";

	/** WebhookTypeId */
	public WebhookTypeId: number

	/** eventType: sensorOffline=1, rule=2, webhook=3 */
	public eventType: number;

	/** rule */
	public rule: Rule;

	/** ruleValuesHtml */
	public ruleValuesHtml?: SafeHtml;

	/** ruleCondition */
	public ruleCondition: string = "";

	/** asset */
	public asset: LevelAsset;

	/** comments */
	public comments: EventComment[];

	/** start time for viewing the asset chart */
	public startTime: number;
	/** endTime */
	public endTime: number;

	/** accountID */
	@Input() accountID: string;

	/** AccountFeatures */
	AccountFeatures = AccountFeatures;

	/** selectedAccount */
	public selectedAccount: IAccount;

	/** OrganisationID */
	@Input() public OrganisationUID: string;

	/** Acknowledged */
	public Acknowledged: boolean = true;

	/** thisUser */
	public thisUser: WebAppUser;

	/** Timeline */
	public Timeline: TimelineItem[] = [];

	/** actionTypes - values correspond to value in db table */
	public actionTypes: IActionType[] = []

	/** Debugging */
	public displayTimeline: boolean = true;

	/** True if the selected account is not the account that the event exists on */
	public wrongAccount: boolean = false;

	/** Whether the comment accordion should open automatically */
	public expanded: boolean = false;

	/** Chart Zoom Settings */
	public zoomSettings: Object;
	/** Sensor X Axis */
	public sensorXAxis: Object;
	/** Sensor Y Axis */
	public sensoryAxis: object;
	/** Sensor Y Average Axis */
	public sensoryAxis_Avg: object
	/** Chart Legend */
	public legend: Object;
	/** Sensor Data Label */
	public dataLabels: Object;
	/** Tooltip Settings */
	public tooltipSettings: Object;
	/** Chart boarder Settings */
	public borderSettings: Object;

	/** crosshair */
	public crosshair: object = {
		enable: true,
		lineType: 'Vertical'
	};

	/**
	 * Chart Selection mode
	 * None|Point|Series|Cluster|DragXY|DragX|DragY
	 */
	public selectionMode: string;

	/** Sensor pen Pallet Colours */
	public palette: object;

	public chartWidth: string = "750px";
	public chartHeight: string = "275px";

	/** My Chart */
	@ViewChild('myChart2') public myChart: ChartComponent;

	private seriesColors: { datapointID: number, color: string }[] = [];

	/** showChart */
	public showChart: boolean = false;

	/** parsedData */
	public parsedData: ISensorData[] = [];

	/** used for displaying min and max for datapoints */
	public uniqueDPs: any[];

	/** Data Point Values */
	public dataPointValues: object[];

	private lightGreenCyan: string = "#00E1B3";         // Lighter green/cyan color
	private lightGreenCyanDarker1: string = "#02735c";  // Noticeably darker version of light green/cyan
	private lightGreenCyanDarker2: string = "#005244";  // Even darker version of light green/cyan

	private darkBlue: string = "#3366FF";               // Slightly lighter dark blue color
	private darkBlueDarker1: string = "#022073";        // Darker version of dark blue
	private darkBlueDarker2: string = "#010b2b";        // Even darker version of dark blue

	// Predefined array of default colors
	private defaultColors: string[] = [
		"#FF5733", // Red
		"#33FF57", // Green
		"#3357FF", // Blue
		"#F0E130", // Yellow
		"#FF33F0", // Pink
		"#33FFF6", // Aqua
		"#FF9833", // Orange
		"#F633FF", // Purple
		"#8BFF33"  // Light Green
	];

	// Variable to track the next default color to use
	private defaultColorIndex: number = 0;

	public primaryMax: number = 0;
	public secondaryMax: number = 0;

	/** width of graph window in hours */
	public windowWidth: number = 24;

	/** Spinner */
	public spinner: boolean

	/** strDuration */
	public strDuration: string = '01:00:00'; //'';

	/** visibleStartDate */
	public visibleStartDate: Date;
	/** visibleEndDate */
	public visibleEndDate: Date;

	/** shortMonths */
	private shortMonths: string[] = [
		"Jan", "Feb", "Mar", "Apr", "May", "Jun",
		"Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
	];

	/** Define start date */
	public startDate: Date;
	/** Define end date */
	public endDate: Date;

	/** existingDistribution */
	public existingDistribution: AssetDistribution;

	/** dpsThatTriggeredRule */
	public dpsThatTriggeredRule: AssetDataPoint[] = [];

	/** ruleTextRegex */
	public ruleTextRegex: RegExp = /\d+(\.\d+)?/g;

	/**
	 * Default Constructor
	 * @param rulesDataService rulesDataService
	 * @param assetsDataService assetsDataService
	 * @param dialog dialog
	 * @param authenticationService authenticationService
	 * @param siteSubscriptions siteSubscriptions
	 * @param router router
	 * @param globalFunctions globalFunctions
	 */
	constructor(
		public rulesDataService: RulesDataService,
		public eventsDataService: EventsDataService,
		public assetsDataService: AssetsDataService,
		public modelsService: ModelsService,
		public dataPointsDataService: DatapointsDataService,
		public dialog: MatDialog,
		public authenticationService: AuthenticationService,
		public siteSubscriptions: siteSubscriptions,
		public actionsDataService: ActionsDataService,
		public accountsService: AccountsService,
		private router: Router,
		private route: ActivatedRoute,
		private globalFunctions: GlobalFunctions,
		private location: Location,
		private sanitizer: DomSanitizer
	) {

		this.globalFunctions.displaySpinner(true);

		combineLatest([
			this.authenticationService.currentUser$.pipe(distinctUntilChanged()),
			this.route.paramMap.pipe(distinctUntilChanged()),
			this.route.queryParamMap.pipe(distinctUntilChanged()),
			this.actionsDataService.getActionTypes$().pipe(distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([user, paramMap, queryParamMap, ActionTypes]) =>
				!!user && !!paramMap && !!queryParamMap && !!ActionTypes),
			takeUntilDestroyed(this.destroyRef)
		).subscribe({
			next: async ([user, paramMap, queryParamMap, ActionTypes]) => {

				let [selectedAccount, selectedAccountGUID, selectedOrganisation, selectedOrganisationUID] = await firstValueFrom(
					combineLatest([
						this.siteSubscriptions.selectedAccount$.pipe(take(1)),
						this.siteSubscriptions.selectedAccountGUID$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisation$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisationUID$.pipe(take(1))
					]
					)
				);

				this.actionTypes = ActionTypes

				var eventID = paramMap.get('eventID') ?? "";

				var eventType_S = queryParamMap.get('eventType') ?? ""
				this.eventType = parseInt(eventType_S)

				this.selectedAccount = selectedAccount;
				this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;

				this.thisUser = user;

				let expanded = queryParamMap.get('expanded');
				if (expanded == "true") {
					this.expanded = true;
				}

				let triedAccountGUID: string = "";
				let tempAcc = paramMap.get('account');
				let tempOrg = paramMap.get('organisation');

				if (tempAcc && tempOrg) {
					triedAccountGUID = <string>tempAcc;
				}

				let skipGettingEvent: boolean = false;

				if (triedAccountGUID.toLowerCase() != selectedAccountGUID.toLowerCase()) {
					console.log("wrong account signed in");
					skipGettingEvent = true;
					this.wrongAccount = true;

					/* redirect to the routing page if on the wrong account */
					this.router.navigateByUrl(`routing#${this.router.url}`);
				}

				this.accountID = selectedAccountGUID;
				this.OrganisationUID = selectedOrganisationUID;

				if (!skipGettingEvent) {

					if (this.eventType === 1) {
						try {
							const offlineEvent = await firstValueFrom(
								this.eventsDataService.getOfflineEvents$(this.accountID, this.OrganisationUID, parseInt(eventID))
							);

							if (offlineEvent?.length) {
								this.offlineEvent = offlineEvent[0];
								this.Acknowledged = this.offlineEvent.Acknowledged;
								this.globalFunctions.displaySpinner(false);
								this.getEventComments();
							}
						}
						catch (error: any) {
							if (error.status == 404) { }
							else { throw error; }
						} finally { this.globalFunctions.displaySpinner(false); }
					}

					if (this.eventType === 2) {
						try {
							const ruleEvents = await firstValueFrom(
								this.eventsDataService.getRuleEvents$(this.accountID, this.OrganisationUID, 0, parseInt(eventID))
							);

							if (ruleEvents?.length) {
								this.ruleEvent = ruleEvents[0];
								this.endTime = this.ruleEvent.TimeStamp;
								this.Acknowledged = this.ruleEvent.Acknowledged;

								// this.CMMSCase = this.ruleEvent.CaseID ? this.ruleEvent.CaseID : undefined
								if (!this.ruleEvent.RuleText.toLowerCase().includes("between")) {
									this.showChart = true;
								}
								this.tooltipSettings = {
									enable: true,
									//shared: true // Enables shared tooltip
								};
								this.zoomSettings = {
									mode: 'X',
									// enableMouseWheelZooming: true,
									enablePinchZooming: true,
									enableSelectionZooming: true,
									enableScrollbar: true
								};
								this.getRule();
								this.getEventComments();
								this.getTimeline();
								this.setXAxis();
							}
						}
						catch (error: any) {
							if (error.status == 404) { }
							else { throw error; }
						} finally { this.globalFunctions.displaySpinner(false); }
					}

					if (this.eventType === 3) {
						try {
							const webhookEvents = await firstValueFrom(
								this.eventsDataService.getWebhookEvents$(this.accountID, this.OrganisationUID, parseInt(eventID))
							);

							if (webhookEvents?.length) {
								this.webhookEvent = webhookEvents[0];
								this.endTime = this.webhookEvent.TimeStamp;
								if (this.webhookEvent.WebhookEventType == 1 || this.webhookEvent.WebhookEventType == 0) {
									this.webhookEventType = "Offline"
								}
								this.getEventComments();
							}
						}
						catch (error: any) {
							if (error.status == 404) { }
							else { throw error; }
						} finally { this.globalFunctions.displaySpinner(false); }
					}

					if (this.eventType === 4) {
						try {
							const deviationEvents = await firstValueFrom(
								this.eventsDataService.getDeviationEvents$(this.accountID, this.OrganisationUID, 0, parseInt(eventID))
							);

							if (deviationEvents?.length) {
								this.deviationEvent = deviationEvents[0];
								this.Acknowledged = this.deviationEvent.Acknowledged;
								this.getAssetFromID(this.deviationEvent.AssetID);
								this.getDistribution(this.deviationEvent.AssetDistributionID);
								this.getEventComments();
							}
						}
						catch (error: any) {
							if (error.status == 404) { }
							else { throw error; }
						} finally { this.globalFunctions.displaySpinner(false); }

					}
				} else {
					this.globalFunctions.displaySpinner(false);
				}
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * getRuleValuesHtml
	 * @paramvalue
	 */
	getRuleValuesHtml(value: string): SafeHtml {
		return this.sanitizer.bypassSecurityTrustHtml(value);
	}

	/**
	 * getRule
	 */
	async getRule() {

		try {
			const rules = await firstValueFrom(
				this.rulesDataService.getRules$(this.accountID, this.OrganisationUID, 0, this.ruleEvent.RuleID)
			);

			if (rules?.length) {
				this.rule = rules[0]
				this.ruleValuesHtml = this.sanitizer.bypassSecurityTrustHtml(this.ruleEvent.RuleValues);

				if (this.rule.RuleText.includes("AND")) {
					this.ruleCondition = "AND";
				} else if (this.rule.RuleText.includes("OR")) {
					this.ruleCondition = "OR";
				}
			}
			if (this.rule.TriggerTime < 10) { //show 10 mins on graph if trigger time is too small
				this.startTime = this.endTime - ((10 * 60) * 1000)
			}
			else {
				this.startTime = this.endTime - ((this.rule.TriggerTime * 60) * 1000)
			}

			this.getAssetFromID(this.rule.AssetID)

		}
		catch (error: any) {
			if (error.status == 404) { }
			else { this.globalFunctions.HandleError(error, "Error: this.rulesDataService.getRules$", false); }
		}

	}

	/** getEventComments */
	async getEventComments(acknowledgeAfterComment?: boolean, reopenAfterComment?: boolean) {

		this.comments = [];
		let eventID: number = 0;

		if (this.eventType === 2) { eventID = this.ruleEvent.RuleEventID }
		else if (this.eventType === 1) { eventID = this.offlineEvent.OfflineEventID }
		else if (this.eventType === 3) { eventID = this.webhookEvent.WebhookEventID }
		else if (this.eventType === 4) { eventID = this.deviationEvent.DeviationEventID }

		try {
			const eventComments = await firstValueFrom(
				this.eventsDataService.getEventComment$(this.accountID, this.OrganisationUID, eventID, this.eventType)
			);

			if (eventComments) {
				this.comments = eventComments;
				if (acknowledgeAfterComment) {
					this.acknowledgeEvent(false);
				}
				if (reopenAfterComment) {
					this.reopenEvent(false);
				}
			}
		}
		catch (error: any) {
			if (error.status == 404) { }
			else { this.globalFunctions.HandleError(error, "Error: this.eventsDataService.getEventComment$", false); }
		}

	}

	/**
	 * getAssetFromID
	 * @param asset
	 */
	async getAssetFromID(assetID: number) {

		try {
			const organisationAssets = await firstValueFrom(
				this.assetsDataService.getOrganisationAssets$(this.accountID, this.OrganisationUID, assetID)
			);

			if (organisationAssets) {
				this.asset = organisationAssets;
				this.getAssetDataPoints(this.asset);
			}
			this.globalFunctions.displaySpinner(false);
		}
		catch (error: any) {
			if (error.status == 404) { }
			else { this.globalFunctions.HandleError(error, "Error: AssetsDataService.getOrganisationAssets$", false); }
		}

	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpochString(_epoch: string): string {
		/** Use the Global Function */
		let epoch: number = parseInt(_epoch);
		return convertEpochToDateString(epoch);
	}

	/** Return to the events page on the correct tab */
	goToEvents() {
		let tab: string = "";
		switch (this.eventType) {
			case 2:
				tab = "Rule"
				break;
			case 1:
				tab = "Sensor";
				break;
			case 3:
				tab = "Webhook";
				break;
			default:
				break;
		}
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/event`], {
			queryParams: { selectedTab: tab }
		});
	}

	/**
	 * back
	 */
	back() {
		this.location.back();
	}

	/** acknowledgeEvent */
	async acknowledgeEvent(addComment: boolean) {

		if (!addComment) {

			let watchedData: WatchedAssetData;

			if (this.eventType === 2 || this.eventType === 4) {

				try {
					const watchedAssetData = await firstValueFrom(
						this.assetsDataService.getWatchedAssetData$(this.accountID, this.OrganisationUID, this.asset.AssetID)
					);

					if (watchedAssetData?.length) {
						watchedData = watchedAssetData[0];

						if (!watchedData.WatchListAssetID) {
							return;
						}

						let ruleEventIDs: number[] = [];
						let newRuleEventIDs: number[] = [];
						let sensorEventIDs: number[] = [];
						let deviationEventIDs: number[] = [];
						let newDeviationEventIDs: number[] = [];

						if (this.eventType === 2 && watchedData.TriggeredRules) {
							let parsedEvents: RuleEvent[] = JSON.parse(watchedData.TriggeredRules);
							parsedEvents.forEach(event => {
								ruleEventIDs.push(Number(event.RuleEventID))
							});
							newRuleEventIDs = ruleEventIDs.filter((x: number) => x != this.ruleEvent.RuleEventID)
							watchedData.TriggeredRules = newRuleEventIDs.toString();
							if (watchedData.TriggeredDataPoints) {
								sensorEventIDs = JSON.parse(watchedData.TriggeredDataPoints).map((x: OfflineEvent) => Number(x.OfflineEventID));
							}
							watchedData.TriggeredDataPoints = sensorEventIDs.toString();
							if (watchedData.TriggeredDeviations) {
								deviationEventIDs = JSON.parse(watchedData.TriggeredDeviations).map((x: DeviationEvent) => Number(x.DeviationEventID));
							}
							watchedData.TriggeredDeviations = deviationEventIDs.toString();
						}

						else if (this.eventType === 4 && watchedData.TriggeredDeviations) {
							let parsedEvents: DeviationEvent[] = JSON.parse(watchedData.TriggeredDeviations);
							parsedEvents.forEach(event => {
								deviationEventIDs.push(Number(event.DeviationEventID))
							});
							newDeviationEventIDs = deviationEventIDs.filter((x: number) => x != this.deviationEvent.DeviationEventID)
							watchedData.TriggeredDeviations = newDeviationEventIDs.toString();
							if (watchedData.TriggeredRules) {
								ruleEventIDs = JSON.parse(watchedData.TriggeredRules).map((x: RuleEvent) => Number(x.RuleEventID));
							}
							watchedData.TriggeredRules = ruleEventIDs.toString();
							if (watchedData.TriggeredDataPoints) {
								sensorEventIDs = JSON.parse(watchedData.TriggeredDataPoints).map((x: OfflineEvent) => Number(x.OfflineEventID));
							}
							watchedData.TriggeredDataPoints = sensorEventIDs.toString();
						}

						try {
							const response = await firstValueFrom(
								this.assetsDataService.patchWatchedAssetData$(this.accountID, this.OrganisationUID, watchedData.WatchListAssetID, watchedData)
							);

							if (response) {
								this.globalFunctions.DisplaySnackBar({
									message: "Event Acknowledged",
									duration: 2000,
									type: SnackType.Info,
									action: ""
								})
								this.Acknowledged = true;
								if (this.eventType === 2) {
									this.getTimeline();
									this.createAction();
								}
							}

						}
						catch (error: any) {
							if (error.status != 404) {
								this.globalFunctions.HandleError(error, "eventDetails Error: Patching Watched Data", false)
							} else {
								this.globalFunctions.DisplaySnackBar({
									message: "Event Acknowledged",
									duration: 2000,
									type: SnackType.Info,
									action: ""
								})
								this.Acknowledged = true;
							}
						}
					}

				}
				catch (error: any) {
					if (error.status != 404) {
						this.globalFunctions.HandleError(error, "eventDetails Error: Acknowledging Rule", false)
					} else {
						this.globalFunctions.DisplaySnackBar({
							message: "Event Already Acknowledged",
							duration: 1000,
							type: SnackType.Info,
							action: ""
						})
					}
				}
			}
			else if (this.eventType === 1) {

				try {
					const response = await firstValueFrom(
						this.eventsDataService.acknowledgeSensorEventForAllAssets$(this.accountID, this.OrganisationUID, this.offlineEvent.SensorID, this.offlineEvent.OfflineEventID)
					);

					if (response?.length) {
						this.globalFunctions.DisplaySnackBar({
							message: response[0].Result,
							duration: 2000,
							type: SnackType.Info,
							action: ""
						})
						this.Acknowledged = true;
					}

				}
				catch (error: any) {
					if (error.status != 404) {
						this.globalFunctions.HandleError(error, "eventDetails Error: Acknowledging SensorEvent", false)
					} else {
						this.globalFunctions.DisplaySnackBar({
							message: "Event Already Acknowledged",
							duration: 1000,
							type: SnackType.Info,
							action: ""
						})
					}
				}
			}
		} else {
			this.addCommentNew(true);
		}
	}

	/**
	 * splitValues
	 * @param text text
	 * @returns
	 */
	splitValues(text: string): string[] { return text.split(';') }

	/**
	 * ViewRule
	 * @param rule rule
	 */
	ViewRule(rule: Rule) {
		var _edit = { ...rule };

		let time: string = this.convertMinutesToHHMM(_edit.TriggerTime)[0] + ":" + this.convertMinutesToHHMM(_edit.TriggerTime)[1]

		let dialogData = <EditRuleDialogData>{
			assetItem: this.asset,
			ruleItem: _edit,
			triggerTime: time,
			comparison: _edit.RuleText.match(/AND|OR/)?.[0] || "",
			readonly: !this.selectedAccount.Admin
		}

		const dialogRef = this.dialog.open(CreateRuleComponent, {
			data: dialogData
		});

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(async result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			if (result.ruleEnabled == null) {
				rule.RuleEnabled = false;
			} else {
				rule.RuleEnabled = result.ruleEnabled
			}
			rule.RuleDescription = result.ruleDescription
			rule.RuleMessage = result.ruleMessage
			rule.TriggerTime = this.timeToMinutes(result.ruleTime)

			/* split the rule into the rule-definition composition */

			if (rule.RuleDef) {
				const data1 = {
					RuleDefID: rule.RuleDef[0].RuleDefID,
					RuleID: rule.RuleID,
					Condition: 1,
					DatapointID: result.dataPoint1,
					Limit: result.value1,
					Operator: result.condition1
				}
				let definition1: RuleDefinition = data1;

				let definition2 = undefined;
				/** if we already had another definition */
				if (result.dataPoint2 && rule.RuleDef[1]) {
					const data2 = {
						RuleDefID: rule.RuleDef[1].RuleDefID,
						RuleID: rule.RuleID,
						Condition: 2,
						DatapointID: result.dataPoint2,
						Limit: result.value2,
						Operator: result.condition2
					}
					definition2 = data2;
					/** if a new definition is being added */
				} else if (result.dataPoint2 && !rule.RuleDef[1]) {
					const data2 = {
						RuleID: rule.RuleID,
						Condition: 2,
						DatapointID: result.dataPoint2,
						Limit: result.value2,
						Operator: result.condition2
					}
					definition2 = data2;
				}

				if (definition2) {
					rule.RuleText = "[1] " + result.comparison + " [2]";
					rule.RuleDef = [definition1, definition2];

				} else {
					rule.RuleText = "[1]"
					rule.RuleDef = [definition1]
				}
			}
			await this.patchRule(rule);
			let start: number = this.ruleEvent.TimeStamp - ((this.windowWidth / 2) * 60 * 60 * 1000);
			let end: number = this.ruleEvent.TimeStamp + ((this.windowWidth / 2) * 60 * 60 * 1000);
			this.GetDataPointValues(this.asset, start, end);
		});
	}

	/**
	 * Converts the HH:MM format required for the trigger time to an int of
	 * total minutes
	 * @param timeStr timeStr
	 * @returns total minutes
	 */
	timeToMinutes(timeStr: string): number {
		const [hours, minutes] = timeStr.split(':').map(Number);
		const totalMinutes = hours * 60 + minutes;
		return totalMinutes;
	}

	/**
	 * patchRule
	 * @param rule rule
	 */
	async patchRule(rule: Rule) {

		try {
			const response = await firstValueFrom(
				this.rulesDataService.patchRule$(this.accountID, this.OrganisationUID, rule.AssetID, rule, this.thisUser.UserID)
			);

			if (response) {
				this.globalFunctions.DisplaySnackBar({
					message: "Rule Updated",
					type: SnackType.Info,
					duration: 1000
				});
			}
			this.getTimeline();

		}
		catch (error: any) {
			if (error.status == 404) { }
			else { this.globalFunctions.HandleError(error, "Error: this.rulesDataService.patchRule$", false); }
		}

	}


	/**
	 * Converts int of total minutes to [HH,MM] string array
	 * @param totalMinutes totalMinutes
	 * @returns HH:MM string
	 */
	convertMinutesToHHMM(totalMinutes: number): string[] {
		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;
		const hoursString = hours.toString().padStart(2, '0');
		const minutesString = minutes.toString().padStart(2, '0');
		return [hoursString, minutesString]
	}

	/**
	 * EditComment
	 * @param comment commemnt
	 * @param event event
	 */
	async EditComment(comment: EventComment, event: any) {
		if (event.value != "" && event.value != comment.CommentText) {

			comment.CommentText = event.value;

			try {
				const response = await firstValueFrom(
					this.eventsDataService.patchEventComment$(this.accountID, this.OrganisationUID, comment, this.eventType)
				);

				if (response) {
					await this.getEventComments()
					this.globalFunctions.DisplaySnackBar({
						message: "Comment Updated",
						type: SnackType.Info,
						duration: 1000
					});
				}

			}
			catch (error: any) {
				if (error.status == 404) { }
				else { this.globalFunctions.HandleError(error, "Error: this.eventsDataService.patchEventComment$", false); }
			}

		}
	}

	/** addCommentNew */
	addCommentNew(acknowledgeAfterComment?: boolean, reopenAfterComment?: boolean) {
		const dialogRef = this.dialog.open(CommentDialogComponent, {
			width: '500px',
			data: {
				"HeaderText": "Add Comment",
				"DialogText": "Add a Comment?"
			}
		});
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(async result => {
			// Perform the delete action with the comment
			// console.log('The dialog was closed with comment:', result.comment);
			// Add your delete logic here
			let _comment = result.comment;

			let eventID: number = 0;
			if (this.eventType === 2) { eventID = this.ruleEvent.RuleEventID }
			else if (this.eventType === 1) { eventID = this.offlineEvent.OfflineEventID }
			else if (this, this.eventType === 3) { eventID = this.webhookEvent.WebhookEventID }
			else if (this, this.eventType === 4) { eventID = this.deviationEvent.DeviationEventID }

			let comment: EventComment = {
				CommentText: _comment,
				EventID: 0,
				EventType: this.eventType
			}

			comment.EventID = eventID;
			try {
				const response = await firstValueFrom(
					this.eventsDataService.postEventComment$(this.accountID, this.OrganisationUID, comment.EventID, comment, this.eventType, this.thisUser.UserID)
				);

				if (response) {
					await this.getEventComments(acknowledgeAfterComment, reopenAfterComment);
					this.globalFunctions.DisplaySnackBar({
						message: "Comment Added",
						type: SnackType.Info,
						duration: 1000
					});
					if (this.eventType === 2) {
						await this.getTimeline();
					}
				}
			}
			catch (error: any) {
				if (error.status == 404) { }
				else { this.globalFunctions.HandleError(error, "Error: this.eventsDataService.postEventComment$", false); }
			}

		});
	}

	/**
	 * reopenEvent
	 */
	async reopenEvent(addComment: boolean) {
		if (this.ruleEvent) {
			if (!this.checkIfAnyOutstandingActions()) {
				if (!addComment) {

					try {
						const response = await firstValueFrom(
							this.eventsDataService.reopenRuleEvent$(this.accountID, this.OrganisationUID, this.ruleEvent.RuleEventID)
						);

						if (response) {
							this.globalFunctions.DisplaySnackBar({
								message: "Event Re-Opened",
								type: SnackType.Info,
								duration: 1000
							});
							this.Acknowledged = false;
							await this.getTimeline();
						}
					}
					catch (error: any) {
						if (error.status == 404) { }
						else { this.globalFunctions.HandleError(error, "Error: this.eventsDataService.reopenRuleEvent$", false); }
					}

				} else {
					this.addCommentNew(false, true)
				}
			} else {
				this.globalFunctions.DisplaySnackBar({
					message: "Cannot Re-Open with outstanding action",
					type: SnackType.Info,
					duration: 2000
				});
			}
		}
	}

	/**
	 * createAction
	 */
	createAction() {
		let dialogData = <createActionDialogData>{
			assetItem: this.asset,
			isAttachedtoEvent: true
		}

		const dialogRef = this.dialog.open(CreateActionComponent, {
			data: dialogData
		});
		dialogRef.afterClosed().pipe(
		).subscribe(async result => {
			if (!result) {
				this.createAction();
			} else {
				if (!result.skipAction) {
					let newAction: Action = {
						ActionID: 0,
						ActionType: result.actionType,
						AssetID: this.asset.AssetID,
						AssetName: `${this.asset.Name} - ${this.asset.Description}`,
						EventID: this.ruleEvent.RuleEventID,
						TimeLineID: this.ruleEvent.TimelineID,
						Timestamp: 0,
						Completed: result.isHistoric,
						Details: result.details,
						CompletedTimestamp: result.isHistoric ? result.targetCompletionTimestamp.getTime() : 0,
						CompletedByUserID: result.isHistoric ? result.assignedToUser : '',
						CreatedByUserSub: '',
						AssignedToUserSub: result.assignedToUser,
						WorkOrderReference: result.workOrderReference,
						TargetCompletionTimestamp: result.targetCompletionTimestamp.getTime(),
						DisabledRules: result.ruleList ? result.ruleList.toString() : ''
					}
					let disable;
					if (result.ruleList && result.ruleList.length > 0) {
						disable = true;
					}

					try {
						const response = await firstValueFrom(
							this.actionsDataService.postAction$(this.accountID, this.OrganisationUID, newAction, disable)
						);

						if (response) {
							this.globalFunctions.DisplaySnackBar({
								message: "Action Created",
								type: SnackType.Info,
								duration: 1000
							});
						}
						await this.getTimeline();

					}
					catch (error: any) {
						if (error.status == 404) { }
						else { this.globalFunctions.HandleError(error, "Error: this.actionsDataService.postAction$", false); }
					}

				}
			}
		});
	}

	public timelineItems: TimelineItemModel[] = [];

	/**
	 * getTimeline
	 */
	async getTimeline() {

		try {
			const timeline = await firstValueFrom(
				this.eventsDataService.getTimeline$(this.accountID, this.OrganisationUID, this.ruleEvent.TimelineID)
			);

			if (timeline) {
				this.Timeline = JSON.parse(timeline[0].Data);
				this.timelineItems = this.Timeline.map(({ Category, Content, Timestamp }) => ({
					dotCss: Category.toString(),
					content: Content,
					oppositeContent: Timestamp.toString(),
					object: JSON.parse(Content)
				}));
			}
		}
		catch (error: any) {
			if (error.status == 404) { }
			else { this.globalFunctions.HandleError(error, "Error: this.eventsDataService.getTimeline$", false); }
		}

	}

	/**
	 * buildObject
	 */
	buildObject(content: string, _category: string): any {
		let category: number = parseInt(_category);
		if (category == 1 || category == 2 || category == 3) {
			let result = <RuleEvent[]>JSON.parse(content);
			return result[0];
		} else if (category == 4 || category == 6) {
			let result = <EventComment[]>JSON.parse(content);
			return result[0];
		} else if (category == 5 || category == 7) {
			let result = <Action[]>JSON.parse(content);
			return result[0];
		} else if (category == 8 || category == 9) {
			let result = <Rule[]>JSON.parse(content);
			return result[0];
		} else if (category == 10) {
			let result = <RuleEdit[]>JSON.parse(content);
			return result[0];
		}
	}

	/**
	 * getActionTypeFromValue
	 * @param value value 
	 * @returns 
	 */
	getActionTypeFromValue(value: number): string {
		let result = this.actionTypes.find((x: IActionType) => x.ActionTypeID == value)?.Description;
		if (result) {
			return result;
		} else {
			return "";
		}
	}

	/**
	 * Goes to the assets functional location instead of straight to the asset details page
	 */
	goToAssetLocation() {
		this.siteSubscriptions.SetSelectedAsset(this.asset);
		this.router.navigate([`/${this.accountID}/organisation/${this.OrganisationUID}/location/${this.asset.ParentUID}/asset/${this.asset.AssetID}`]);
	}

	/**
	 * goToTimelineItem
	 * @param timelineItem timelineItem 
	 */
	goToTimelineItem(timelineItem: any) {
		let category: number = parseInt(timelineItem.dotCss);
		if (category == 1 || category == 2 || category == 3) {
			let result = <RuleEvent[]>JSON.parse(timelineItem.content);
			this.router.navigate(
				[`/${this.accountID}/organisation/${this.OrganisationUID}/event/${result[0].RuleEventID}`],
				{ queryParams: { eventType: 2 } }
			);
		} else if (category == 4) {
			let result = <EventComment[]>JSON.parse(timelineItem.content);
			this.router.navigate(
				[`/${this.accountID}/organisation/${this.OrganisationUID}/event/${result[0].EventID}`],
				{ queryParams: { eventType: 2, expanded: true } }
			);
		} else if (category == 6) {
			let result = <EventComment[]>JSON.parse(timelineItem.content);
			this.router.navigate(
				[`/${this.accountID}/organisation/${this.OrganisationUID}/actions/${result[0].EventID}`],
				{ queryParams: { expanded: true } }
			);
		} else if (category == 5 || category == 7) {
			let result = <Action[]>JSON.parse(timelineItem.content);
			this.router.navigate(
				[`/${this.accountID}/organisation/${this.OrganisationUID}/actions/${result[0].ActionID}`]
			);
		} else if (category == 8 || category == 9) {
			let result = <Rule[]>JSON.parse(timelineItem.content);
			this.router.navigate(
				[`/${this.accountID}/organisation/${this.OrganisationUID}/rules/${result[0].RuleID}`]
			);
		} else if (category == 10) {
			let result = <RuleEdit[]>JSON.parse(timelineItem.content);
			this.router.navigate(
				[`/${this.accountID}/organisation/${this.OrganisationUID}/rules/${result[0].RuleID}`]
			);
		}
	}

	public assetDataPoints: AssetDataPoint[] = [];


	/**
	 * 	getAssetDataPoints
	 * @param asset asset 
	 */
	async getAssetDataPoints(asset: LevelAsset) {

		this.displaySpinner(true);

		try {
			const datapoints = await firstValueFrom(
				this.assetsDataService.getAssetDataPoints$(this.accountID, this.OrganisationUID, asset.AssetID)
			);

			if (datapoints) {
				this.assetDataPoints = datapoints;

				if (this.eventType === 2 && this.ruleEvent) {
					let start: number = this.ruleEvent.TimeStamp - ((this.windowWidth / 2) * 60 * 60 * 1000);
					let end: number = this.ruleEvent.TimeStamp + ((this.windowWidth / 2) * 60 * 60 * 1000);
					this.startDate = new Date(start);
					this.endDate = new Date(end)
					this.visibleStartDate = this.startDate;
					this.visibleEndDate = this.endDate;
					this.GetDataPointValues(asset, start, end);
				}

			}

		}
		catch (error: any) {
			if (error.status == 404) { }
			else { this.globalFunctions.HandleError(error, "Error: this.assetsDataService.getAssetDataPoints$", false); }
		} finally {
			this.displaySpinner(false);
		}
	}

	/**
	 * Get Values for the DataPoints
	 * @param asset
	 * @param dateStart
	 * @param dateEnd
	 * @returns
	 */
	async GetDataPointValues(asset: LevelAsset, dateStart: any, dateEnd: any): Promise<boolean> {

		if (this.myChart) {
			this.myChart.primaryXAxis.zoomFactor = 1;
			this.myChart.primaryXAxis.zoomPosition = 0;
		}

		try {
			const dataPointValues = await firstValueFrom(
				this.assetsDataService.getAssetDataPointValues$(this.accountID, this.OrganisationUID, asset.AssetID, dateStart, dateEnd)
			);

			if (dataPointValues) {
				try {

					let parsedData: ISensorData[];

					//get asset datapoints 
					parsedData = dataPointValues.map((dataPointValue: any) => {
						return {
							//AssetId: dataPointValue.AssetID,
							AssetId: asset.AssetID,
							timestamp: dataPointValue.Timestamp,
							sampleDate: new Date(dataPointValue.Timestamp),
							...dataPointValue.Values
						}
					});

					// console.log("Parsed DataPoints")
					this.parsedData = parsedData;

					let ruleDatapoints: AssetDataPoint[] = this.assetDataPoints.filter((x: AssetDataPoint) => this.checkDatapointsOnRule(x, this.rule));

					this.dpsThatTriggeredRule = ruleDatapoints.filter((x: AssetDataPoint) => this.checkDatapointTriggeredRule(x, this.rule));

					// Extract unique pairs of Si and Pv
					let uniqueDPPairs = [...new Set(ruleDatapoints.map((x: DataPoint) => `${x.DataPointID},${x.SI},${x.EngMin},${x.EngMax}`))];
					// console.log(this.dataPoints)
					// let uniqueSIPairs = [...new Set(this.dataPoints.map((x: DataPoint) => `${x.SI},${x.PrimaryIndex}`))];
					let uniqueSIPairs = [
						...new Set(
							ruleDatapoints
								.sort((a: DataPoint, b: DataPoint) => {
									const aPrimaryIndex = a.PrimaryIndex ? "true" : "false";
									const bPrimaryIndex = b.PrimaryIndex ? "true" : "false";
									return aPrimaryIndex.localeCompare(bPrimaryIndex) * -1;
								})
								/* functionality for issue #83 */
								.filter((dp: AssetDataPoint) => this.dpsThatTriggeredRule.includes(dp))
								.map((x: DataPoint) => `${x.SI},${this.dpsThatTriggeredRule.length == 1 ? true : x.PrimaryIndex}`)
						)
					];

					// let uniqueSIPairs = [...new Set(this.dataPoints.map((x: DataPoint) => `${x.SI}`))];

					// parse the unique pairs back into separate Datapoints
					let uniqueDP = uniqueDPPairs.map((pair: any) => {
						let [dp, si, EngMin, EngMax] = pair.split(",");
						return { Dp: dp, Si: si, Max: EngMax, Min: EngMin };
					});

					this.uniqueDPs = uniqueDP;

					// // Calculate the Min and Max from the Actual Data based on the Datapoint						
					// let calcMinMax = uniqueDP.map((DP: any, index) => {
					// 	const values = parsedData
					// 		.map(item => item[`[${DP.Dp}]`])
					// 		.filter(value => typeof value === 'number') as number[];
					// 	// Find the minimum value
					// 	if (values.length != 0) {
					// 		// This is commented out as it caused errors on long periods
					// 		// DP.Min = Math.min(...values);
					// 		// DP.Max = Math.max(...values);
					// 		// This works around the issue
					// 		DP.Min = values.reduce((min, val) => val < min ? val : min, values[0]);
					// 		DP.Max = values.reduce((max, val) => val > max ? val : max, values[0]);
					// 	} else {
					// 		DP.Min = 0;
					// 		DP.Max = 0;
					// 	}
					// });

					// parse the unique pairs back into separate Si and Pv values
					let uniqueSI = uniqueSIPairs.map((pair: any) => {
						let [si, primary] = pair.split(",");
						return { Si: si, Primary: primary };
					});

					// // parse the unique pairs back into separate Si and Pv values
					// let uniqueSI = uniqueSIPairs.map((pair: any) => {
					// 	let [si] = pair.split(",");
					// 	return { Si: si};
					// });

					// Create axes for each unique unit
					let axes = uniqueSI.map((unit: any, index) => {

						// Calculate the Min and Max from the SI data in the chart
						let { min, max, interval } = this.findMinMaxForSi(uniqueDP, unit.Si);

						if (min == 0 && max == 1) {
							max = 2;
							interval = 1;
						}

						// Build Axis
						let axis: any = {
							// Assuming you want each axis to have a unique name based on the unit
							name: `Axis${unit.Si}`,
							// You can add more properties here if needed

							majorTickLines: { width: 0 },
							minorTickLines: { width: 0 },

							majorGridLines: { width: 0 },
							minorGridLines: { width: 0 },

							minimum: min,
							maximum: max, /** Let the Chart calculate the Max */

							interval: interval,//0.2,
							rowIndex: 1,
							lineStyle: { width: 1 },
							labelFormat: `{value} ${unit.Si}`,
						};

						// Set opposedPosition to true only for the none primary Value
						if (unit.Primary != 'true') {
							axis.opposedPosition = true;
							axis.rowIndex = 0;
						}
						return axis;
					});

					// this.myChart.primaryYAxis.visible=false;
					if (axes[0]) {
						axes[0].majorGridLines = { width: 1 };
						axes[0].opposedPosition = false;
						this.myChart.primaryYAxis = axes[0];

						//this.myChart.primaryXAxis.majorGridLines = { width: 0 };

						// Creating an array of the remaining elements of 'axes'
						let remainingAxes = axes.slice(1);
						// Assigning the remaining axes to the 'axes' property of 'myChart'
						this.myChart.axes = remainingAxes;

					} else {
						this.myChart.primaryYAxis = {};
						this.myChart.axes = [];
					}

					// Clear previous series
					this.myChart.clearSeries()

					// Add new Series
					this.dpsThatTriggeredRule.forEach((dPoint: AssetDataPoint) => {
						this.plotIfSelected(dPoint);
					});

					let ruleEvents: RuleEvent[] = [];
					ruleEvents.push(this.ruleEvent);
					this.plotRuleEvents(ruleEvents, this.rule);

					this.ruleSelected(this.rule);

					return true;

				} catch (error) {
					console.error(error);
					return false;
				}
			} else { return false; }

		}
		catch (error: any) {
			this.dataPointValues = [];
			this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getAssetDataPointValues$", false)
			return false;
		} finally {
			this.displaySpinner(false);
		}
	}

	/** plotIfSelected */
	plotIfSelected(dPoint: AssetDataPoint) {
		if (this.checkDatapointsOnRule(dPoint, this.rule)) {
			if (this.dpsThatTriggeredRule.includes(dPoint)) {

				// Determine the color based on the content of dPoint.SI
				let seriesColor = 'red';
				let existingColor: boolean = false;
				let findExistingColor = this.seriesColors.find((x) => x.datapointID == dPoint.DataPointID);
				if (findExistingColor && findExistingColor.color) {
					seriesColor = findExistingColor.color;
					existingColor = true;
				}
				if (!existingColor) {
					if (dPoint.SI.includes('g')) {
						// Check if lightGreenCyan has already been used
						if (this.seriesColors.some(s => s.color === this.lightGreenCyan)) {
							// If used, choose a darker shade
							seriesColor = this.seriesColors.some(s => s.color === this.lightGreenCyanDarker1)
								? this.lightGreenCyanDarker2
								: this.lightGreenCyanDarker1;
						} else {
							seriesColor = this.lightGreenCyan;
						}
					} else if (dPoint.SI.includes('C')) {
						// Check if darkBlue has already been used
						if (this.seriesColors.some(s => s.color === this.darkBlue)) {
							// If used, choose a darker shade
							seriesColor = this.seriesColors.some(s => s.color === this.darkBlueDarker1)
								? this.darkBlueDarker2
								: this.darkBlueDarker1;
						} else {
							seriesColor = this.darkBlue;
						}
					} else {
						// If no specific condition is met, use the next color in the default array
						seriesColor = this.defaultColors[this.defaultColorIndex];

						// Increment the defaultColorIndex, and loop back to 0 if we reach the end of the array
						this.defaultColorIndex = (this.defaultColorIndex + 1) % this.defaultColors.length;
					}
				}

				let seriesName = dPoint.SI == '' ? dPoint.DPTitle : `${dPoint.DPTitle} (${dPoint.SI})`
				this.myChart.addSeries([{
					animation: {
						enable: false
					},
					emptyPointSettings: { mode: "Drop" },
					type: 'Line',
					dataSource: this.parsedData,
					name: seriesName,
					xName: 'timestamp',
					yName: `[${dPoint.DataPointID}]`,
					marker: {
						visible: false,
						width: 8,
						height: 8,
						shape: 'Triangle',
						dataLabel: { "name": 'x' },
						isFilled: true
					},
					yAxisName: `Axis${dPoint.SI}`,
					fill: seriesColor
				}]);
				if (!existingColor) {
					this.seriesColors.push({
						datapointID: dPoint.DataPointID,
						color: seriesColor
					});
				}

				this.primaryMax = <number>this.myChart.primaryYAxis.maximum;

				if (this.myChart.axes[0]) {
					this.secondaryMax = <number>this.myChart.axes[0].maximum;
				}
			}
		}
	}

	/**
	 * checkDatapointsOnRule
	 * @param datapoint datapoint 
	 * @param rule rule 
	 * @returns 
	 */
	checkDatapointsOnRule(datapoint: DataPoint, rule: Rule): boolean {
		if (rule.RuleDef && rule.RuleDef[0].DatapointID == datapoint.DataPointID) {
			return true;
		} else if (rule.RuleDef && rule.RuleDef.length > 1 && rule.RuleDef[1].DatapointID == datapoint.DataPointID) {
			return true;
		} else {
			return false;
		}
	}

	/**
	 * checkDatapointTriggeredRule
	 * @param datapoint datapoint 
	 * @param rule rule  
	 * @returns 
	 */
	checkDatapointTriggeredRule(datapoint: DataPoint, rule: Rule): boolean {
		let checkingFirstDP: boolean = false;
		if (rule.RuleDef && datapoint.DataPointID == rule.RuleDef[0].DatapointID) {
			checkingFirstDP = true;
		}

		let result: boolean = false;

		if (checkingFirstDP) {
			let valueNum1: number;
			let conditionNum1: number;
			let condition1: string;
			//if the event was triggered after 19/05/2025
			if (this.ruleEvent.TimeStamp > 1747648800000) {
				let _num1 = this.ruleEvent.RuleText.split(" ")[2];
				condition1 = this.ruleEvent.RuleText.split(" ")[1];
				let value1: string = this.ruleEvent.RuleValues.split(";")[0];
				if (Number.isNaN(parseFloat(value1))) {
					let _ = value1.match(/\d+(\.\d+)?/);
					value1 = _ ? _[0] : '';
				}
				conditionNum1 = _num1 ? parseFloat(_num1) : 0;
				valueNum1 = value1 ? parseFloat(value1) : 0;
			}
			else {
				condition1 = this.ruleEvent.RuleText.split(" ")[0];
				let value1: string = this.ruleEvent.RuleValues.split(";")[0];
				let _num11 = value1.match(/\d+(\.\d+)?/);
				let _num1 = condition1.match(/\d+(\.\d+)?/);
				conditionNum1 = _num1 ? parseFloat(_num1[0]) : 0;
				valueNum1 = _num11 ? parseFloat(_num11[0]) : 0;
			}

			if (condition1.includes(">=")) {
				if (valueNum1 >= conditionNum1) {
					result = true;
				}
			} else if (condition1.includes("<=")) {
				if (valueNum1 <= conditionNum1) {
					result = true;
				}
			} else if (condition1.includes(">")) {
				if (valueNum1 > conditionNum1) {
					result = true;
				}
			} else if (condition1.includes("<")) {
				if (valueNum1 < conditionNum1) {
					result = true;
				}
			} else if (condition1.includes("=")) {
				if (valueNum1 == conditionNum1) {
					result = true;
				}
			}
		} else {
			let condition2: string;
			let value2: string;
			let conditionNum2: number;
			let valueNum2: number;
			if (this.ruleEvent.RuleText.includes("AND") || this.ruleEvent.RuleText.includes("OR")) {
				//if the event was triggered after 19/05/2025
				if (this.ruleEvent.TimeStamp > 1747648800000) {
					let _num2 = this.ruleEvent.RuleText.split(" ")[6];
					condition2 = this.ruleEvent.RuleText.split(" ")[5];
					let value2: string = this.ruleEvent.RuleValues.split(";")[1]
					if (Number.isNaN(parseFloat(value2))) {
						let _ = value2.match(/\d+(\.\d+)?/);
						value2 = _ ? _[0] : '';
					}
					conditionNum2 = _num2 ? parseFloat(_num2) : 0;
					valueNum2 = value2 ? parseFloat(value2) : 0;
				} else {
					condition2 = this.ruleEvent.RuleText.split(" ")[2];
					value2 = this.ruleEvent.RuleValues.split(";")[1];
					let _num2 = condition2.match(/\d+(\.\d+)?/);
					conditionNum2 = _num2 ? parseFloat(_num2[0]) : 0;
					let _num22 = value2.match(/\d+(\.\d+)?/);
					valueNum2 = _num22 ? parseFloat(_num22[0]) : 0;
				}

				if (condition2.includes(">=")) {
					if (valueNum2 >= conditionNum2) {
						result = true;
					}
				} else if (condition2.includes("<=")) {
					if (valueNum2 <= conditionNum2) {
						result = true;
					}
				} else if (condition2.includes(">")) {
					if (valueNum2 > conditionNum2) {
						result = true;
					}
				} else if (condition2.includes("<")) {
					if (valueNum2 < conditionNum2) {
						result = true;
					}
				} else if (condition2.includes("=")) {
					if (valueNum2 == conditionNum2) {
						result = true;
					}
				}
			}
		}
		return result;
	}

	/**
	 * REturn the Axis scale Parameters
	 * @param data
	 * @param targetSi
	 * @returns
	 */
	findMinMaxForSi(data: any[], targetSi: string): { min: number | 0, max: number | 999, interval: number | 1 } {
		// Filter the array to include only objects where Si equals the targetSi
		const filtered = data.filter(item => item.Si.toLowerCase() === targetSi.toLowerCase());

		if (filtered.length === 0) {
			return { min: 0, max: 0, interval: 1 }; // No matching objects found
		}

		// Extract Min and Max values from the filtered objects
		const minValues = filtered.map(item => item.Min);
		const maxValues = filtered.map(item => item.Max);

		// Find the minimum and maximum values
		let min = Math.min(...minValues);
		let max = Math.max(...maxValues);

		min = Math.floor(min);
		max = Number(max.toFixed(1));
		let interval = ((max - min) / 10);
		if (interval > 1) {
			interval = Number(interval.toFixed(1));
			interval = Math.ceil(interval)
		} else { interval = this.roundToNearestTenth(interval); }

		max = max + interval;
		interval = (min == max) ? interval = 1 : interval

		return { min, max, interval };
	}

	/** Round to Nearest 10th */
	roundToNearestTenth(num: number): number {
		return Math.ceil(num * 10) / 10;
	}

	/**
	 * Add a section to the graph
	 * @param lines stripLine Definition
	 */
	setXAxis(lines: object[] = []): void {

		/* Sensor Graph specific variables */
		this.sensorXAxis = {
			// title: 'Date',
			valueType: "DateTime", //'Category',
			intervalType: parseInt(this.strDuration.split(":")[0]) > 2 || this.strDuration == 'custom' ? "Days" : "Hours",
			// intervalType: "Hours",
			// interval: 1,
			labelFormat: parseInt(this.strDuration.split(":")[0]) > 2 || this.strDuration == 'custom' ? "dd-MMM" : "HH:mm dd-MMM",
			// labelFormat: "HH:mm:ss dd-MMM",
			// labelIntersectAction: "Rotate45",
			// labelRotation: 35,
			// labelRotation: 90,
			majorTickLines: { visible: false },
			minorTickLines: { visible: false },
			majorGridLines: { visible: false },
			minorGridLines: { visible: false },
			// stripLines: lines

		};
	}

	/** plotRuleEvents */
	plotRuleEvents(ruleEvents: RuleEvent[], rule: Rule) {
		let currentStriplines: StripLineSettingsModel[] = [];
		if (this.myChart.primaryXAxis.stripLines) {
			currentStriplines = this.myChart.primaryXAxis.stripLines;
		}

		ruleEvents.forEach(ruleEvent => {
			currentStriplines.push({
				startFromAxis: false,
				start: ruleEvent.TimeStamp,
				// size: 1,
				end: ruleEvent.TimeStamp + 1000,
				zIndex: 'Over',
				opacity: 1,
				color: 'red',
				border: { color: 'red', width: 2 }
			});
			currentStriplines.push({
				startFromAxis: false,
				start: ruleEvent.TimeStamp - (rule.TriggerTime * 60 * 1000),
				// size: 1,
				end: ruleEvent.TimeStamp,
				zIndex: 'Over',
				opacity: 0.25,
				border: { color: 'red', width: 1 },
				isSegmented: true, // Enable the segmented mode for stripline
				segmentAxisName: 'primaryXAxis', // Replace with the axis name if different
				text: ruleEvent.RuleText, // Add tooltip text
				rotation: -45,
				// verticalAlignment: 'Start',
				textStyle: { color: 'transparent', fontWeight: 'Bold' }
			});
		})

		this.myChart.primaryXAxis.stripLines = currentStriplines;
	}

	/** ruleSelected */
	ruleSelected(rule: Rule) {
		const scrollableDiv = document.getElementById('dockContent');
		const scrollX = scrollableDiv?.scrollLeft || 0;
		const scrollY = scrollableDiv?.scrollTop || 0;

		if (rule.RuleDef) {
			let dashArray: string = "10,5";
			let defaultColour: string = 'orange';
			let useDefaultColour: boolean = this.dpsThatTriggeredRule.length == 1;

			this.dpsThatTriggeredRule.forEach((dp: AssetDataPoint) => {
				const def = rule.RuleDef!.find((x: RuleDefinition) => x.DatapointID == dp.DataPointID);
				if (!def) return;
				const defIndex = rule.RuleDef!.indexOf(def);

				let ruleText: string = this.ruleEvent.RuleText;
				let match = ruleText.match(this.ruleTextRegex);
				let limit: number = match ? parseFloat(match[defIndex]) : 0; // Default to 0 if no match

				let axis = undefined;
				if (this.dpsThatTriggeredRule.length == 1 || dp.PrimaryIndex) {
					axis = this.myChart.primaryYAxis;
				} else {
					axis = this.myChart.axes[0];
				}
				if (!axis) return;

				let size;
				if (axis.interval) {
					size = axis.interval / 10;
				}
				let existingStripLines: StripLineSettingsModel[] = [];
				if (axis.stripLines) {
					existingStripLines = axis.stripLines;
				}

				if (<number>axis.maximum <= limit) {
					axis.interval = (limit - <number>axis.minimum) / 10;
					axis.maximum = limit + <number>axis.interval;
				} else if (<number>axis.minimum >= limit) {
					axis.interval = (<number>axis.maximum - limit) / 10;
					axis.minimum = limit - <number>axis.interval;
				}

				// stopping striplines being too fat
				if (axis.maximum && size && (<number>axis.maximum <= (size * 40))) {
					size /= 5;
				}

				let colour = defaultColour;
				if (!useDefaultColour) {
					let _colour = this.seriesColors.find((x) => x.datapointID == dp.DataPointID);
					if (_colour) colour = _colour.color;
				}

				existingStripLines.push(
					{
						startFromAxis: false,
						start: limit - 0.01,
						size: size,
						zIndex: 'Over',
						opacity: 1,
						color: colour,
						dashArray: dashArray
					}
				);
				axis.stripLines = existingStripLines;
			});
		}
		setTimeout(() => {
			if (scrollableDiv) {
				scrollableDiv.scrollLeft = scrollX;
				scrollableDiv.scrollTop = scrollY;
			}
		}, 0);
	}

	/**
	 * Returns full datapoint object from assets list of datapoints when given
	 * a DataPointID, or undefined if the DataPointID does not match
	 * @param datapointID
	 * @returns
	 */
	getDatapointInAssetDataPoints(datapointID: number): AssetDataPoint | undefined {
		return this.assetDataPoints.find((x: AssetDataPoint) => x.DataPointID == datapointID)
	}

	/**
	 * Show the Spinner
	 * @param show
	 */
	displaySpinner(show: boolean) {
		this.spinner = show;
	}

	/**
	 * checkIfAnyOutstandingActions
	 */
	checkIfAnyOutstandingActions(): boolean {
		let result: boolean = true;
		let actionsCreated: number = this.Timeline.filter((x: TimelineItem) => x.Category == 5).length;
		let actionsCompleted: number = this.Timeline.filter((x: TimelineItem) => x.Category == 7).length;
		if (actionsCreated == actionsCompleted) {
			result = false;
		}
		return result;
	}

	/**
	 * selectTime
	 */
	selectTime(sDuration: string) {
		this.strDuration = sDuration;
		let values: number[] = this.getWindowFromDuration(sDuration);
		let start: number = values[0];
		let end: number = values[1];
		this.startDate = new Date(start);
		this.endDate = new Date(end);
		this.visibleStartDate = this.startDate;
		this.visibleEndDate = this.endDate;
		if (this.myChart) {
			this.myChart.clearSeries();
			this.myChart.primaryYAxis.stripLines = [];
			this.myChart.primaryXAxis.stripLines = [];
		}
		this.displaySpinner(true);
		this.setXAxis();
		this.GetDataPointValues(this.asset, start, end);
	}

	/**
	 * getWindowFromDuration
	 * @param duration duration 
	 */
	getWindowFromDuration(duration: string): number[] {
		let result: number[] = [0, 0];
		let parts: string[] = duration.split(":");
		let width: number = parseInt(parts[1]);
		if (width == 0) {
			width = parseInt(parts[0]) * 24;
		}
		let start: number = this.ruleEvent.TimeStamp - ((width / 2) * 60 * 60 * 1000);
		let end: number = this.ruleEvent.TimeStamp + ((width / 2) * 60 * 60 * 1000);
		let now: number = new Date().getTime();
		if (end > now) {
			end = now;
			start = end - ((width) * 60 * 60 * 1000);
		}
		result = [start, end];
		return result;
	}

	/** getShortDate */
	getShortDate(date: Date): string {
		let result: string = "";
		result += date.getDate();
		result +=
			" " +
			this.shortMonths[date.getMonth()] +
			" " +
			date.getHours().toString().padStart(2, "0") +
			":" +
			date.getMinutes().toString().padStart(2, "0");

		return result;
	}

	/**
	 * onZoom
	 * @param event 
	 */
	onZoom(event: any) {
		if (event.axis.orientation != 'Vertical') {
			if (event.currentZoomFactor != 1) {
				this.visibleStartDate = new Date(event.currentVisibleRange.min);
				this.visibleEndDate = new Date(event.currentVisibleRange.max);
			} else {
				this.visibleStartDate = this.startDate;
				this.visibleEndDate = this.endDate;
			}
		}
	}

	/**
	 * onScroll
	 * @param event 
	 */
	onScroll(event: any) {
		if (event.axis.orientation != 'Vertical') {
			if (event.zoomFactor != 1) {
				this.visibleStartDate = new Date(event.range.min);
				this.visibleEndDate = new Date(event.range.max);
			} else {
				this.visibleStartDate = this.startDate;
				this.visibleEndDate = this.endDate;
			}
		}
	}

	/**
	 * checkClick
	 * @param event event  
	 */
	checkClick(event: any) {
		if (event.target.toLowerCase().includes("zooming_reset")) {
			this.visibleStartDate = this.startDate;
			this.visibleEndDate = this.endDate;
		}
	}

	/**
	 * checkNotLast
	 * @param data data 
	 * @returns 
	 */
	checkNotLast(data: any): boolean {
		let result: boolean = false;
		let finalItem: number = this.Timeline.length - 1;
		if (data.itemIndex == finalItem) {
			result = true;
		}
		return result;
	}

	/**
	 * onTooltipRender
	 * @param args args 
	 */
	onTooltipRender(args: any): void {
		let timestamp: Date = args.data.pointX;
		const day = timestamp.getDate();
		const monthIndex = timestamp.getMonth();
		const minutes = timestamp.getMinutes();
		const hours = timestamp.getHours();
		const seconds = timestamp.getSeconds();
		const formattedDate = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')} ${day} ${this.shortMonths[monthIndex]}`;
		args.text = `${formattedDate} :${args.text.split(":").pop()}`;
	}

	/**
	 * viewDistribution
	 */
	viewDistribution() {
		let data = {
			asset: this.asset,
			viewing: true,
			distributionID: this.deviationEvent.AssetDistributionID,
			existingDistribution: this.existingDistribution
		}

		const dialogRef = this.dialog.open(CreateAssetDistributionComponent
			, { data: data }
		);

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
		});
	}

	/**
	 * viewCMMS
	 */
	viewCMMS() {
		this.router.navigate([
			this.route.snapshot.paramMap.get('account'),
			'organisation',
			this.route.snapshot.paramMap.get('organisation'),
			'SAPCases',
			this.CMMSCase
		]);
	}

	/**
	 * checkFeature
	 * @param feature feature   
	 * @returns 
	 */
	checkFeature(feature: AccountFeatures): boolean {
		if (this.selectedAccount && this.selectedAccount.Features) {
			return AccountsService.checkFeature(feature, this.selectedAccount)
		}
		return false;
	}

	/**
	 * getDistribution
	 * @param distID distID 
	 */
	async getDistribution(distID: number) {

		try {
			const distribution = await firstValueFrom(
				this.modelsService.getAssetDistribution$(this.accountID, this.OrganisationUID, 0, distID)
			);

			if (distribution) {
				this.existingDistribution = distribution[0];
			}

		}
		catch (error: any) {
			if (error.status == 404) { }
			else { this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getAssetDistribution$", false); }
		}

	}
}
